import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { NgForm } from "@angular/forms";
import { Subscription } from "rxjs";
import { AuthService, ConsentValidateResponse } from "../authServices";
import { NgxSpinnerService } from "ngx-spinner";
import { ToastrService } from 'ngx-toastr';
import SignaturePad from 'signature_pad';

@Component({
  selector: 'vex-digital-service-form',
  templateUrl: './digital-service-form.component.html',
  styleUrls: ['./digital-service-form.component.scss']
})
export class DigitalServiceFormComponent implements OnInit, OnDestroy {
  hide = true;
  showPassword = false;
  isLoading = false;
  private authStatusSub!: Subscription;
  signatureNeeded = false;
  signaturePad!: SignaturePad;
  @ViewChild('canvas') canvasEl!: ElementRef;
  signatureImg = '';
  currentDate = new Date().toLocaleDateString('en-US', { timeZone: 'UTC' });
  type!: string;
  name!: string;
  recipientName: string | null = null;
  firm: string | null = null;
  encounterId: any;
  applicantDigitalService: string | null = null;
  isDigitalServiceAccepted = false;
  email: any;
  fullName: any;
  useTokenFlow = false;
  linkError: string | null = null;
  private consentToken = '';

  get displayName(): string | null {
    return this.type === 'Applicant' ? this.fullName : this.recipientName || this.name;
  }

  constructor(
    public authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    this.authStatusSub = this.authService.getAuthStatusListener().subscribe(() => {
      this.isLoading = false;
    });

    this.route.queryParams.subscribe(params => {
      if (params['token']) {
        this.useTokenFlow = true;
        this.consentToken = params['token'];
        this.loadTokenFlow();
        return;
      }

      this.type = params['type'];
      this.name = params['name'];
      this.recipientName = params['recipient'] || null;
      this.firm = params['firm'] || null;
      this.email = params['email'];
      this.encounterId = params['encounterId'];
      this.fullName = params['full'];
    });
  }

  private loadTokenFlow() {
    this.isLoading = true;
    this.authService.validateConsentToken(this.consentToken).subscribe({
      next: (response: ConsentValidateResponse) => {
        this.type = response.partyType;
        this.name = response.name;
        this.recipientName = response.recipientName || null;
        this.fullName = response.fullName;
        this.email = response.email;
        this.firm = response.firm || null;
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        const message =
          error?.error?.message ||
          (error?.status === 410
            ? 'This invitation link has expired or was already used.'
            : 'This invitation link is invalid.');
        this.linkError = message;
        this.toastr.error(message, 'Unable to open form', {
          timeOut: 5000,
          closeButton: true,
        });
      },
    });
  }

  onSave(form: NgForm) {
    if (form.invalid || this.signaturePad.isEmpty()) {
      this.signatureNeeded = true;
      return;
    }

    this.isLoading = true;
    this.signatureImg = this.signaturePad.toDataURL();
    this.applicantDigitalService = "Accepted";
    this.submitForm();
  }

  submitForm() {
    const request$ = this.useTokenFlow
      ? this.authService.acceptConsentToken(this.consentToken, this.signatureImg)
      : this.authService.add(
          this.name,
          this.type,
          this.encounterId,
          this.applicantDigitalService,
          this.signatureImg
        );

    request$.subscribe({
      next: () => {
        this.isLoading = false;
        this.toastr.success("Signature submitted successfully!", "Success!", {
          timeOut: 3000,
          closeButton: true,
        });
        this.router.navigate(['/thankyou']);
      },
      error: (error) => {
        this.isLoading = false;
        const message =
          error?.error?.message ||
          'Unable to submit your signature. Please try again or contact OrthoLegal.';
        this.toastr.error(message, 'Submission failed', {
          timeOut: 5000,
          closeButton: true,
        });
      },
    });
  }

  ngAfterViewInit() {
    this.signaturePad = new SignaturePad(this.canvasEl.nativeElement);
  }

  startDrawing(_event: Event) {
    // works in device not in browser
  }

  moved(_event: Event) {
    // works in device not in browser
  }

  clearPad() {
    this.signaturePad.clear();
    this.signatureImg = '';
  }

  ngOnDestroy() {
    this.authStatusSub?.unsubscribe();
  }
}
